import React from 'react';
import { useAppContext } from '@/context/app';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { useNavigate } from 'react-router-dom';

type UseApiHook = {
    token?: string;
    apiBase: string;
    buildUri: <Q = Record<string, string>>(path: string, query?: Q) => string;
    apiPost: <B = unknown, R = unknown>(
        path: string,
        body: B,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiPut: <B = unknown, R = unknown>(
        path: string,
        body: B,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiPatch: <B = unknown, R = unknown>(
        path: string,
        body: B,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiGet: <R = unknown, Q = Record<string, string | string[]>>(
        path: string,
        query?: Q,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;
    apiDelete: <R = unknown>(
        path: string,
        commonHeaders?: Record<string, string>,
        signal?: AbortSignal | null | undefined,
    ) => Promise<R>;

    // checkTokenExpiry: () => Promise<{ isExpired: boolean; session: CognitoUserSession; }>
    // refreshToken: (session?: CognitoUserSession) => Promise<void>
};

type CognitoUserSession = Awaited<ReturnType<typeof Auth.currentSession>>;

export interface IApiError {
    status: number;
    error: unknown;
}

const base = `${import.meta.env.VITE_APP_REST_API_DOMAIN}`;

const useApi = (apiBase: string = base): UseApiHook => {
    const navigate = useNavigate();
    const { auth, setAuthUserInfo } = useAppContext();
    const { token } = auth;

    const buildUri = <Q = Record<string, string>>(
        path: string,
        query?: Q,
    ): string => {
        const fullUrl = `${apiBase}${path}`;

        if (!query || Object.keys(query).length === 0) {
            return fullUrl;
        }

        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value != null) {
                params.append(key, String(value));
            }
        });

        return `${fullUrl}?${params.toString()}`;
    };

    const handleResponse = async <R>(response: Response): Promise<R> => {
        if (response.ok) {
            if (response.status === 204) return undefined as R;

            return response.json();
        }

        const errorData = await response
            .json()
            .catch(() => ({ message: 'Unknown error' }));
        const error: IApiError = {
            status: response.status,
            error: errorData,
        };
        throw new Error(JSON.stringify(error));
    };

    const createRequestConfig = (
        method: string,
        body?: unknown,
        commonHeaders: Record<string, string> = {},
        signal?: AbortSignal | null | undefined,
    ): RequestInit => {
        const config: RequestInit = {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...commonHeaders,
            },
            signal,
        };

        if (body !== undefined) {
            config.body = JSON.stringify(body);
        }

        return config;
    };

    const refreshToken = async (
        session?: CognitoUserSession,
    ): Promise<void> => {
        if (!session) session = await Auth.currentSession();
        const refreshToken = session.getRefreshToken();
        const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;

        return new Promise((resolve, reject) => {
            user.refreshSession(refreshToken, (refreshErr, newSession) => {
                if (refreshErr) {
                    console.error('Error refreshing session:', refreshErr);
                    reject();
                } else {
                    setAuthUserInfo({
                        ...auth,
                        token: newSession.getIdToken().getJwtToken(),
                        email: newSession.getIdToken().decodePayload()[
                            'email'
                        ] as string,
                    });
                    resolve();
                }
            });
        });
    };

    const checkTokenExpiry = async (): Promise<{
        isExpired: boolean;
        session: CognitoUserSession;
    }> => {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken();
        const expiryTime = idToken.getExpiration();
        const currentTime = Math.floor(Date.now() / 1000);

        return { isExpired: currentTime >= expiryTime, session };
    };

    const fetchWithFailHandler = async <R>(
        url: string,
        config: RequestInit,
    ): Promise<R> => {
        console.log(config.signal);

        try {
            const response = await fetch(url, config);

            return await handleResponse<R>(response);
        } catch (error) {
            const { isExpired, session } = await checkTokenExpiry();
            if (isExpired) {
                await refreshToken(session).catch(() => {
                    Auth.signOut({ global: true });
                    apiPut<{ state: string }, void>('/users/me', {
                        state: 'disconnected',
                    });
                    Promise.resolve().finally(() => navigate('/auth/login'));

                    console.error('Session expired, log in again.');
                    throw new Error('Session expired, please log in again.');
                });

                return handleResponse<R>(await fetch(url, config));
            } else {
                throw error;
            }
        }
    };

    const apiPost = React.useCallback(
        async <B, R>(
            path: string,
            body: B,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('POST', body, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiPut = React.useCallback(
        async <B, R>(
            path: string,
            body: B,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('PUT', body, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiPatch = React.useCallback(
        async <B, R>(
            path: string,
            body: B,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('PATCH', body, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiGet = React.useCallback(
        async <R, Q = Record<string, string | string[]>>(
            path: string,
            query?: Q,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            // console.log(signal);

            return fetchWithFailHandler<R>(
                buildUri(path, query),
                createRequestConfig('GET', undefined, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    const apiDelete = React.useCallback(
        async <R>(
            path: string,
            commonHeaders: Record<string, string> = {},
            signal?: AbortSignal | null | undefined,
        ): Promise<R> => {
            return fetchWithFailHandler<R>(
                buildUri(path),
                createRequestConfig('DELETE', undefined, commonHeaders, signal),
            );
        },
        [token, apiBase],
    );

    return {
        token,
        apiBase,
        buildUri,
        apiPost,
        apiPut,
        apiPatch,
        apiGet,
        apiDelete,
    };
};

export default useApi;
